import React from "react";
import { PageHeader } from "../../field-service/components/PageHeader/PageHeader";
import { AskAIPanel } from "../../field-service/components/AskAIPanel/AskAIPanel";

// ─── Types ────────────────────────────────────────────────────────────────────

type TagType = "team" | "product";
type ViewType = "list" | "table" | "board" | "map" | "calendar";

interface Asset {
  id: string;
  name: string;
  serial: string;
  location: string;
  tag?: TagType;
  hasProductBadge?: boolean;
  subAssets?: Asset[];
}

// ─── Mock Data ────────────────────────────────────────────────────────────────


const MOCK_ASSETS: Asset[] = [
  {
    id: "1",
    name: "Hydraulic Press MK-3000",
    serial: "HP3K-001XP",
    location: "Plant A",
    tag: "team",
    subAssets: [
      {
        id: "1-1",
        name: "Press Module Alpha",
        serial: "PMA-001XP",
        location: "Plant A",
        tag: "product",
        subAssets: [
          {
            id: "1-1-1",
            name: "Cylinder Unit A",
            serial: "CUA-001XP",
            location: "Plant A",
            tag: "team",
          },
          {
            id: "1-1-2",
            name: "Cylinder Unit B",
            serial: "CUB-002XP",
            location: "Plant A",
            tag: "team",
          },
        ],
      },
    ],
  },
  {
    id: "2",
    name: "CNC Machining Center X7",
    serial: "CNCX7-002XP",
    location: "Workshop B",
    tag: "product",
    hasProductBadge: true,
    subAssets: [
      {
        id: "2-1",
        name: "Spindle Assembly Unit",
        serial: "SPA-001XP",
        location: "Workshop B",
        tag: "team",
      },
      {
        id: "2-2",
        name: "Tool Changer Module",
        serial: "TCU-001XP",
        location: "Workshop B",
        tag: "product",
      },
      {
        id: "2-3",
        name: "Coolant System",
        serial: "CS-001XP",
        location: "Workshop B",
      },
    ],
  },
  {
    id: "3",
    name: "Industrial Robot Arm R-200",
    serial: "IRA-R200XP",
    location: "Assembly Line 1",
    tag: "team",
    hasProductBadge: true,
    subAssets: [
      {
        id: "3-1",
        name: "Gripper End Effector",
        serial: "GEE-001XP",
        location: "Assembly Line 1",
        tag: "product",
      },
      {
        id: "3-2",
        name: "Joint Motor Set",
        serial: "JMS-001XP",
        location: "Assembly Line 1",
        tag: "team",
      },
      {
        id: "3-3",
        name: "Control Unit V2",
        serial: "CUV2-001XP",
        location: "Assembly Line 1",
      },
    ],
  },
  {
    id: "4",
    name: "Conveyor Belt System CB-5",
    serial: "CBS-005XP",
    location: "Warehouse Zone B",
    tag: "product",
  },
  {
    id: "5",
    name: "Laser Cutting Machine LC-100",
    serial: "LCM-100XP",
    location: "Fabrication Hall",
  },
  {
    id: "6",
    name: "Air Compressor Unit AC-50",
    serial: "ACU-050XP",
    location: "Utility Room",
    tag: "team",
  },
];

// ─── Tag Config ───────────────────────────────────────────────────────────────

const TAG_CONFIG: Record<TagType, { bg: string; color: string; label: string }> = {
  team:    { bg: "#fde5f0", color: "#ee0064", label: "TEAM" },
  product: { bg: "#e6e8fe", color: "#0517f8", label: "PRODUCT" },
};

// ─── Icons ────────────────────────────────────────────────────────────────────

function ChevronUpIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 10l4-4 4 4" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

function QRCodeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="5.5" height="5.5" rx="1" stroke="#748094" strokeWidth="1.3" />
      <rect x="9.5" y="1" width="5.5" height="5.5" rx="1" stroke="#748094" strokeWidth="1.3" />
      <rect x="1" y="9.5" width="5.5" height="5.5" rx="1" stroke="#748094" strokeWidth="1.3" />
      <rect x="2.5" y="2.5" width="2.5" height="2.5" fill="#748094" />
      <rect x="11" y="2.5" width="2.5" height="2.5" fill="#748094" />
      <rect x="2.5" y="11" width="2.5" height="2.5" fill="#748094" />
      <path d="M9.5 9.5h2M13 9.5h2M9.5 12h1M12 12h1M14 12h1M9.5 14.5h1M11.5 14.5h1M13.5 14.5h1" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function DotsMoreIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="#748094">
      <circle cx="4.5" cy="10" r="1.5" />
      <circle cx="10" cy="10" r="1.5" />
      <circle cx="15.5" cy="10" r="1.5" />
    </svg>
  );
}

function AssetOutlineIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 8C0 3.58172 3.58172 0 8 0H40C44.4183 0 48 3.58172 48 8V40C48 44.4183 44.4183 48 40 48H8C3.58172 48 0 44.4183 0 40V8Z" fill="#E6E8FE"/>
      <g clipPath="url(#clip0_5756_12972)">
        <path d="M23.4664 14.7822L20.775 17.4736C20.4821 17.7665 20.4821 18.2413 20.775 18.5342L23.4644 21.2237C23.7573 21.5166 24.2322 21.5166 24.525 21.2237L27.2165 18.5322C27.5094 18.2394 27.5094 17.7645 27.2165 17.4716L24.527 14.7822C24.2341 14.4893 23.7593 14.4893 23.4664 14.7822Z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M29.4672 20.7822L26.7758 23.4736C26.4829 23.7665 26.4829 24.2413 26.7758 24.5342L29.4653 27.2237C29.7581 27.5166 30.233 27.5166 30.5259 27.2237L33.2173 24.5322C33.5102 24.2394 33.5102 23.7645 33.2173 23.4716L30.5279 20.7822C30.235 20.4893 29.7601 20.4893 29.4672 20.7822Z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M17.4664 20.7743L14.775 23.4658C14.4821 23.7587 14.4821 24.2335 14.775 24.5264L17.4644 27.2159C17.7573 27.5088 18.2322 27.5088 18.5251 27.2159L21.2165 24.5244C21.5094 24.2315 21.5094 23.7567 21.2165 23.4638L18.527 20.7743C18.2342 20.4814 17.7593 20.4814 17.4664 20.7743Z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M23.4672 26.7743L20.7758 29.4658C20.4829 29.7587 20.4829 30.2335 20.7758 30.5264L23.4653 33.2159C23.7581 33.5088 24.233 33.5088 24.5259 33.2159L27.2173 30.5244C27.5102 30.2315 27.5102 29.7567 27.2173 29.4638L24.5279 26.7743C24.235 26.4814 23.7601 26.4814 23.4672 26.7743Z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      </g>
      <defs>
        <clipPath id="clip0_5756_12972">
          <rect width="24" height="24" fill="white" transform="translate(12 12)"/>
        </clipPath>
      </defs>
    </svg>
  );
}

function ProductCatalogIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="1" width="4" height="4" rx="0.5" />
      <rect x="7" y="1" width="4" height="4" rx="0.5" />
      <rect x="1" y="7" width="4" height="4" rx="0.5" />
      <rect x="7" y="7" width="4" height="4" rx="0.5" />
    </svg>
  );
}

function ListViewIcon({ active }: { active: boolean }) {
  const c = active ? "#0517f8" : "#a2aab8";
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" strokeLinecap="round">
      <rect x="2" y="3" width="2" height="2" rx="0.5" fill={c} />
      <path d="M6.5 4h7.5" stroke={c} strokeWidth="1.4" />
      <rect x="2" y="7" width="2" height="2" rx="0.5" fill={c} />
      <path d="M6.5 8h7.5" stroke={c} strokeWidth="1.4" />
      <rect x="2" y="11" width="2" height="2" rx="0.5" fill={c} />
      <path d="M6.5 12h7.5" stroke={c} strokeWidth="1.4" />
    </svg>
  );
}

function TableViewIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={active ? "#0517f8" : "#a2aab8"} strokeWidth="1.4" strokeLinecap="round">
      <rect x="2" y="2" width="12" height="12" rx="1.5" />
      <path d="M2 6h12M6 6v8" />
    </svg>
  );
}

function BoardViewIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={active ? "#0517f8" : "#a2aab8"} strokeWidth="1.4" strokeLinecap="round">
      <rect x="2" y="2" width="5" height="12" rx="1" />
      <rect x="9" y="2" width="5" height="7" rx="1" />
    </svg>
  );
}

function CalendarViewIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={active ? "#0517f8" : "#a2aab8"} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="12" height="11" rx="1.5" />
      <path d="M2 7h12" />
      <rect x="5" y="1" width="1.3" height="3" rx="0.65" fill={active ? "#0517f8" : "#a2aab8"} stroke="none" />
      <rect x="9.7" y="1" width="1.3" height="3" rx="0.65" fill={active ? "#0517f8" : "#a2aab8"} stroke="none" />
    </svg>
  );
}

function MapViewIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={active ? "#0517f8" : "#a2aab8"} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2L2 4v10l4-2 4 2 4-2V2l-4 2-4-2z" />
      <path d="M6 2v10M10 4v10" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4h12M4 8h8M6 12h4" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667Z" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 14l-2.9-2.9" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ImportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2v8M5 7l3 3 3-3" />
      <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" />
    </svg>
  );
}

function AskAIIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      {/* Large 4-pointed sparkle */}
      <path
        d="M6.5 2.5 C7.5 6 7.5 6 11 7 C7.5 8 7.5 8 6.5 11.5 C5.5 8 5.5 8 2 7 C5.5 6 5.5 6 6.5 2.5Z"
        fill="#172b4d"
      />
      {/* Small 4-pointed sparkle top-right */}
      <path
        d="M12.5 1 C12.9 2.6 12.9 2.6 14.5 3 C12.9 3.4 12.9 3.4 12.5 5 C12.1 3.4 12.1 3.4 10.5 3 C12.1 2.6 12.1 2.6 12.5 1Z"
        fill="#172b4d"
      />
    </svg>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TagBadge({ type }: { type: TagType }) {
  const cfg = TAG_CONFIG[type];
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px 6px",
      borderRadius: 4,
      background: cfg.bg,
      color: cfg.color,
      fontSize: 12,
      fontWeight: 700,
      lineHeight: "16px",
      letterSpacing: "-0.24px",
      fontFamily: "var(--font-family-base)",
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}>
      {cfg.label}
    </span>
  );
}

function AssetImage({ asset }: { asset: Asset }) {
  return (
    <div style={{
      position: "relative",
      width: 48,
      height: 48,
      borderRadius: 8,
      flexShrink: 0,
      overflow: "hidden",
    }}>
      <AssetOutlineIcon />
      {asset.hasProductBadge && (
        <div style={{
          position: "absolute",
          bottom: 2,
          right: 2,
          background: "#172b4d",
          borderRadius: 4,
          padding: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16,
          height: 16,
        }}>
          <ProductCatalogIcon />
        </div>
      )}
    </div>
  );
}

interface AssetCardProps {
  asset: Asset;
  hasChildren: boolean;
  expanded: boolean;
  onToggle: () => void;
  onOpen?: (id: string, name: string) => void;
}

function AssetCard({ asset, hasChildren, expanded, onToggle, onOpen }: AssetCardProps) {
  const subCount = asset.subAssets?.length ?? 0;
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen?.(asset.id, asset.name)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: 16,
        background: hovered ? "var(--color-bg-accent)" : "white",
        border: "1px solid #e8eaed",
        borderRadius: 8,
        width: "100%",
        boxSizing: "border-box",
        transition: "background 80ms ease",
        cursor: onOpen ? "pointer" : "default",
      }}>
      {/* Left: chevron + image + info */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
        {/* Chevron toggle */}
        {hasChildren && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            style={{
              width: 16,
              height: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              flexShrink: 0,
            }}
          >
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </button>
        )}

        {/* Image + text */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 0 }}>
          <AssetImage asset={asset} />

          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              lineHeight: "24px",
              letterSpacing: "-0.32px",
              color: "#172b4d",
              fontFamily: "var(--font-family-base)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}>
              {asset.name}
            </p>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 0,
              flexWrap: "nowrap",
            }}>
              <span style={{
                fontSize: 12,
                fontWeight: 500,
                lineHeight: "16px",
                color: "#748094",
                fontFamily: "var(--font-family-base)",
                whiteSpace: "nowrap",
              }}>
                {asset.serial}
              </span>
              <span style={{ color: "#a2aab8", fontSize: 12, lineHeight: "16px" }}>•</span>
              <span style={{
                fontSize: 12,
                fontWeight: 500,
                lineHeight: "16px",
                color: "#748094",
                fontFamily: "var(--font-family-base)",
                whiteSpace: "nowrap",
              }}>
                {asset.location}
              </span>
              {subCount > 0 && (
                <>
                  <span style={{ color: "#a2aab8", fontSize: 12, lineHeight: "16px" }}>•</span>
                  <span style={{
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: "16px",
                    color: "#748094",
                    fontFamily: "var(--font-family-base)",
                    whiteSpace: "nowrap",
                  }}>
                    {subCount} Sub Assets
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right: tag + QR + more */}
      {asset.tag && <TagBadge type={asset.tag} />}

      <button style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        background: "white",
        border: "1px solid #e8eaed",
        borderRadius: 8,
        cursor: "pointer",
        padding: 0,
        flexShrink: 0,
      }}>
        <QRCodeIcon />
      </button>

      <button style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 20,
        height: 20,
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 0,
        flexShrink: 0,
      }}>
        <DotsMoreIcon />
      </button>
    </div>
  );
}

interface AssetRowProps {
  asset: Asset;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onOpen?: (id: string, name: string) => void;
}

function AssetRow({ asset, expandedIds, onToggle, onOpen }: AssetRowProps) {
  const hasChildren = !!(asset.subAssets && asset.subAssets.length > 0);
  const isExpanded = expandedIds.has(asset.id);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <AssetCard
        asset={asset}
        hasChildren={hasChildren}
        expanded={isExpanded}
        onToggle={() => onToggle(asset.id)}
        onOpen={onOpen}
      />
      {hasChildren && isExpanded && (
        <div style={{
          display: "flex",
          alignItems: "stretch",
          gap: 0,
        }}>
          {/* Connector column with vertical line */}
          <div style={{
            width: 48,
            flexShrink: 0,
            position: "relative",
          }}>
            <div style={{
              position: "absolute",
              left: 23,
              top: 0,
              bottom: 0,
              width: 1,
              background: "#e8eaed",
            }} />
          </div>

          {/* Sub-assets column */}
          <div style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}>
            {asset.subAssets!.map(sub => (
              <AssetRow key={sub.id} asset={sub} expandedIds={expandedIds} onToggle={onToggle} onOpen={onOpen} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Calendar Types ───────────────────────────────────────────────────────────

type CalViewMode = "week" | "month" | "day";

interface CalWO {
  id: string;
  title: string;
  type: "work-order" | "preventive" | "part-order" | "energy" | "documentation";
  scheduledDate: string;
  dueDate: string;
  assetId: string;
  assignee?: string;
  draft?: boolean;
}

type CalWOState = "assigned" | "unassigned";
type CalPMState = "active" | "upcoming" | "completed";

function calWOState(wo: CalWO): CalWOState {
  return wo.assignee ? "assigned" : "unassigned";
}

function calPMState(pm: CalPMEvent, todayIso: string): CalPMState {
  if (pm.status === "Completed" || pm.status === "Closed") return "completed";
  return pm.triggerDate <= todayIso ? "active" : "upcoming";
}

const DRAFT_TITLES = [
  "Scheduled Inspection",
  "Preventive Maintenance",
  "Equipment Service",
  "Repair Work Order",
  "Routine Checkup",
  "Component Replacement",
  "Safety Audit",
  "System Calibration",
];

const DRAFT_TYPES: CalWO["type"][] = [
  "work-order", "preventive", "part-order", "energy", "documentation",
];

const CAL_MONTH_ASSIGNEES = ["Marcus Webb", "Priya Nair", "Diego Alvarez"];

interface CalPMEvent {
  id: string;
  title: string;
  triggerDate: string;
  status: "Pending" | "Open" | "In Progress" | "Closed" | "Completed";
  assetId: string;
}

interface FlatAsset {
  asset: Asset;
  depth: number;
}

// ─── Calendar Helpers ─────────────────────────────────────────────────────────

function calIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function calDiff(isoA: string, isoB: string): number {
  return Math.round(
    (new Date(isoB + "T00:00:00").getTime() - new Date(isoA + "T00:00:00").getTime()) / 86400000
  );
}

function calAddDays(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return calIso(d);
}

function calWeekDays(anchor: Date): Date[] {
  const s = new Date(anchor);
  const dow = s.getDay();
  s.setDate(s.getDate() - (dow === 0 ? 6 : dow - 1));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(s); d.setDate(d.getDate() + i); return d;
  });
}

function calMonthDays(anchor: Date): Date[] {
  const y = anchor.getFullYear(), m = anchor.getMonth();
  const last = new Date(y, m + 1, 0).getDate();
  return Array.from({ length: last }, (_, i) => new Date(y, m, i + 1));
}

function calWeekRange(days: Date[]): string {
  const f = days[0], l = days[days.length - 1];
  if (f.getMonth() === l.getMonth())
    return `${f.getDate()} – ${l.getDate()} ${f.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`;
  return `${f.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${l.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
}

function calMonthYear(d: Date): string {
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

function flattenAssets(assets: Asset[], expandedIds: Set<string>, depth = 0): FlatAsset[] {
  const result: FlatAsset[] = [];
  for (const asset of assets) {
    result.push({ asset, depth });
    if (asset.subAssets?.length && expandedIds.has(asset.id)) {
      result.push(...flattenAssets(asset.subAssets, expandedIds, depth + 1));
    }
  }
  return result;
}

function assignLanes(items: Array<{ id: string; start: string; end: string }>): Map<string, number> {
  const lanes = new Map<string, number>();
  const laneEnds: string[] = [];
  const sorted = [...items].sort((a, b) => a.start.localeCompare(b.start));
  for (const item of sorted) {
    let lane = laneEnds.findIndex(e => e < item.start);
    if (lane === -1) lane = laneEnds.length;
    lanes.set(item.id, lane);
    laneEnds[lane] = item.end;
  }
  return lanes;
}

// ─── Calendar Config ──────────────────────────────────────────────────────────

// Category colors — used only for the "Type" picker in the create-WO modal,
// not for coloring calendar bars (those are colored by CAL_WO_STATE_CONFIG instead).
const CAL_WO_TYPE: Record<string, { color: string; bg: string }> = {
  "work-order":    { color: "#6974fb", bg: "#e6e8fe" },
  "preventive":    { color: "#00b09b", bg: "#e0faf6" },
  "part-order":    { color: "#ff7a00", bg: "#fff0e6" },
  "energy":        { color: "#10b981", bg: "#e0f8e8" },
  "documentation": { color: "#9333ea", bg: "#f3e8ff" },
};

const CAL_WO_STATE_CONFIG: Record<CalWOState, { color: string; bg: string; label: string }> = {
  assigned:   { color: "#6974fb", bg: "#e6e8fe", label: "Assigned" },
  unassigned: { color: "#ff7a00", bg: "#fff0e6", label: "Unassigned" },
};

const CAL_PM_STATE_CONFIG: Record<CalPMState, { color: string; bg: string; label: string }> = {
  active:    { color: "#b45309", bg: "#fef9c3", label: "Active" },
  upcoming:  { color: "#748094", bg: "#f3f4f6", label: "Upcoming" },
  completed: { color: "#10b981", bg: "#e0f8e8", label: "Completed" },
};

const WO_TYPE_OPTIONS: { value: CalWO["type"]; label: string }[] = [
  { value: "work-order",     label: "Work Order"    },
  { value: "preventive",     label: "Preventive"    },
  { value: "part-order",     label: "Part Order"    },
  { value: "energy",         label: "Energy"        },
  { value: "documentation",  label: "Documentation" },
];

function calFormatDisplayDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

const CAL_ASSET_W = 248;
const CAL_BAR_H = 22;
const CAL_BAR_GAP = 3;
const CAL_ROW_VPAD = 7;
const CAL_DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ─── Calendar Mock Data ───────────────────────────────────────────────────────

const CALENDAR_WOS: CalWO[] = [
  { id: "cwo1",  assetId: "1",     type: "work-order",    title: "Hydraulic System Overhaul",     scheduledDate: calAddDays(-2), dueDate: calAddDays(3), assignee: "Marcus Webb"    },
  { id: "cwo2",  assetId: "1",     type: "documentation", title: "Safety Compliance Report",       scheduledDate: calAddDays(1),  dueDate: calAddDays(6)                              },
  { id: "cwo3",  assetId: "1-1",   type: "part-order",    title: "Press Module Inspection",        scheduledDate: calAddDays(1),  dueDate: calAddDays(4), assignee: "Priya Nair"     },
  { id: "cwo4",  assetId: "1-1-1", type: "work-order",    title: "Cylinder Seal Replacement",      scheduledDate: calAddDays(0),  dueDate: calAddDays(2)                              },
  { id: "cwo5",  assetId: "1-1-2", type: "preventive",    title: "Cylinder B Lubrication",         scheduledDate: calAddDays(3),  dueDate: calAddDays(5), assignee: "Marcus Webb"    },
  { id: "cwo6",  assetId: "2",     type: "work-order",    title: "Spindle Alignment Task",         scheduledDate: calAddDays(-1), dueDate: calAddDays(2), assignee: "Diego Alvarez" },
  { id: "cwo7",  assetId: "2",     type: "preventive",    title: "CNC Calibration Run",            scheduledDate: calAddDays(3),  dueDate: calAddDays(6)                              },
  { id: "cwo8",  assetId: "2-1",   type: "energy",        title: "Spindle Motor Test",             scheduledDate: calAddDays(2),  dueDate: calAddDays(6), assignee: "Priya Nair"     },
  { id: "cwo9",  assetId: "2-2",   type: "part-order",    title: "Tool Changer Calibration",       scheduledDate: calAddDays(4),  dueDate: calAddDays(8)                              },
  { id: "cwo10", assetId: "2-3",   type: "work-order",    title: "Coolant Flush & Refill",         scheduledDate: calAddDays(0),  dueDate: calAddDays(3), assignee: "Diego Alvarez" },
  { id: "cwo11", assetId: "3",     type: "preventive",    title: "Robot Arm Full Inspection",      scheduledDate: calAddDays(1),  dueDate: calAddDays(5)                              },
  { id: "cwo12", assetId: "3-1",   type: "work-order",    title: "Gripper Jaw Replacement",        scheduledDate: calAddDays(-1), dueDate: calAddDays(1), assignee: "Marcus Webb"    },
  { id: "cwo13", assetId: "3-2",   type: "energy",        title: "Joint Motor Torque Check",       scheduledDate: calAddDays(3),  dueDate: calAddDays(7)                              },
  { id: "cwo14", assetId: "3-3",   type: "documentation", title: "Control Unit Firmware Update",   scheduledDate: calAddDays(6),  dueDate: calAddDays(9)                              },
  { id: "cwo15", assetId: "4",     type: "preventive",    title: "Belt Tension Check",             scheduledDate: calAddDays(0),  dueDate: calAddDays(4), assignee: "Priya Nair"     },
  { id: "cwo16", assetId: "5",     type: "work-order",    title: "Laser Lens Cleaning",            scheduledDate: calAddDays(2),  dueDate: calAddDays(5)                              },
  { id: "cwo17", assetId: "6",     type: "part-order",    title: "Compressor Filter Swap",         scheduledDate: calAddDays(1),  dueDate: calAddDays(3)                              },
  // Recurring weekly WOs so the whole month view (not just the days near today) has work orders too.
  ...["1", "2", "3", "4", "5", "6"].flatMap((assetId, assetIdx): CalWO[] =>
    Array.from({ length: 9 }, (_, weekIdx) => {
      const weekOffset = (weekIdx - 3) * 7; // weeks -3 .. +5, spans a full month either side of today
      const title = DRAFT_TITLES[(assetIdx + weekIdx) % DRAFT_TITLES.length];
      const type = DRAFT_TYPES[(assetIdx + weekIdx) % DRAFT_TYPES.length];
      const assigned = (assetIdx + weekIdx) % 2 === 0;
      return {
        id: `cwo-mo-${assetId}-${weekIdx}`,
        assetId,
        type,
        title,
        scheduledDate: calAddDays(weekOffset),
        dueDate: calAddDays(weekOffset + 2),
        ...(assigned ? { assignee: CAL_MONTH_ASSIGNEES[(assetIdx + weekIdx) % CAL_MONTH_ASSIGNEES.length] } : {}),
      };
    })
  ),
];

const CALENDAR_PM_EVENTS: CalPMEvent[] = [
  { id: "cpm1",  assetId: "1",     title: "Monthly Maintenance",   triggerDate: calAddDays(0),  status: "In Progress" },
  { id: "cpm2",  assetId: "1",     title: "Quarterly Maintenance", triggerDate: calAddDays(2),  status: "Pending"     },
  { id: "cpm3",  assetId: "1",     title: "Annual Maintenance",    triggerDate: calAddDays(6),  status: "Pending"     },
  { id: "cpm4",  assetId: "1-1",   title: "Monthly Maintenance",   triggerDate: calAddDays(1),  status: "Open"        },
  { id: "cpm5",  assetId: "1-1-1", title: "Quarterly Maintenance", triggerDate: calAddDays(3),  status: "Pending"     },
  { id: "cpm6",  assetId: "2",     title: "Monthly Maintenance",   triggerDate: calAddDays(1),  status: "Open"        },
  { id: "cpm7",  assetId: "2",     title: "Quarterly Maintenance", triggerDate: calAddDays(3),  status: "Pending"     },
  { id: "cpm8",  assetId: "2-1",   title: "Monthly Maintenance",   triggerDate: calAddDays(2),  status: "Pending"     },
  { id: "cpm9",  assetId: "3",     title: "Monthly Maintenance",   triggerDate: calAddDays(0),  status: "Open"        },
  { id: "cpm10", assetId: "3",     title: "Quarterly Maintenance", triggerDate: calAddDays(2),  status: "In Progress" },
  { id: "cpm11", assetId: "3-2",   title: "Bi-Annual Maintenance", triggerDate: calAddDays(5),  status: "Pending"     },
  { id: "cpm12", assetId: "4",     title: "Monthly Maintenance",   triggerDate: calAddDays(1),  status: "Pending"     },
  { id: "cpm13", assetId: "4",     title: "Quarterly Maintenance", triggerDate: calAddDays(3),  status: "Pending"     },
  { id: "cpm14", assetId: "5",     title: "Monthly Maintenance",   triggerDate: calAddDays(0),  status: "Open"        },
  { id: "cpm15", assetId: "5",     title: "Quarterly Maintenance", triggerDate: calAddDays(2),  status: "Pending"     },
  { id: "cpm16", assetId: "6",     title: "Monthly Maintenance",   triggerDate: calAddDays(1),  status: "In Progress" },
  { id: "cpm17", assetId: "6",     title: "Quarterly Maintenance", triggerDate: calAddDays(3),  status: "Pending"     },
  // Recurring weekly PM so every week (past and future) has at least one event per asset when navigating.
  ...["1", "2", "3", "4", "5", "6"].flatMap((assetId): CalPMEvent[] =>
    Array.from({ length: 10 }, (_, weekIdx) => {
      const weekOffset = (weekIdx - 3) * 7; // weeks -3 .. +6
      const status: CalPMEvent["status"] =
        weekOffset < 0 ? "Completed" : weekOffset === 0 ? "In Progress" : "Pending";
      return {
        id: `cpm-wk-${assetId}-${weekIdx}`,
        assetId,
        title: "Weekly Maintenance",
        triggerDate: calAddDays(weekOffset),
        status,
      };
    })
  ),
];

// ─── Calendar Sub-components ──────────────────────────────────────────────────

function CalNavBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
      background: "#fff", border: "1px solid #e5e9f0", borderRadius: 6,
      cursor: "pointer", padding: 0, color: "#748094",
    }}>
      {children}
    </button>
  );
}

function CalDateCell({ left, width, height, isToday, onClick }: {
  left: number; width: number; height: number; isToday: boolean; onClick: () => void;
}) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "absolute", left, top: 0, width, height,
        background: isToday ? "#f5f7ff" : hovered ? "#fafbff" : "transparent",
        borderRight: "1px solid #f0f2f7", boxSizing: "border-box",
        cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "background 80ms ease",
      }}
    >
      {hovered && (
        <div style={{
          width: 18, height: 18, borderRadius: "50%",
          background: "#e6e8fe",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 400, color: "#6974fb", lineHeight: 1,
          pointerEvents: "none",
        }}>
          +
        </div>
      )}
    </div>
  );
}

function CalBar({ left, width, top, wo, clampedLeft, onClick }: {
  left: number; width: number; top: number; wo: CalWO; clampedLeft: boolean; onClick: () => void;
}) {
  const state = calWOState(wo);
  const conf = CAL_WO_STATE_CONFIG[state];
  const title = `${wo.title} · ${state === "assigned" ? `Assigned to ${wo.assignee}` : "Unassigned"}`;

  if (wo.draft) {
    return (
      <div
        title={title}
        onClick={e => { e.stopPropagation(); onClick(); }}
        style={{
          position: "absolute", left, width, top, height: CAL_BAR_H,
          background: `repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 4px,
            ${conf.color}18 4px,
            ${conf.color}18 8px
          )`,
          border: `1.5px dashed ${conf.color}`,
          borderRadius: 6,
          display: "flex", alignItems: "center",
          padding: "0 8px", overflow: "hidden", cursor: "pointer", boxSizing: "border-box",
        }}>
        <span style={{
          fontSize: 11, fontWeight: 600, color: conf.color,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          fontFamily: "var(--font-family-base)", letterSpacing: "-0.1px",
        }}>
          {wo.title}
        </span>
      </div>
    );
  }

  return (
    <div
      title={title}
      onClick={e => { e.stopPropagation(); onClick(); }}
      style={{
        position: "absolute", left, width, top, height: CAL_BAR_H,
        background: conf.bg,
        borderLeft: clampedLeft ? "none" : `3px solid ${conf.color}`,
        borderRadius: clampedLeft ? "0 6px 6px 0" : 6,
        display: "flex", alignItems: "center",
        padding: "0 8px", overflow: "hidden", cursor: "pointer", boxSizing: "border-box",
      }}>
      <span style={{
        fontSize: 11, fontWeight: 600, color: "#0d2847",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        fontFamily: "var(--font-family-base)", letterSpacing: "-0.1px",
      }}>
        {wo.title}
      </span>
    </div>
  );
}

function CalPMChip({ left, width, top, event, todayIso, onClick }: {
  left: number; width: number; top: number; event: CalPMEvent; todayIso: string; onClick: () => void;
}) {
  const state = calPMState(event, todayIso);
  const conf = CAL_PM_STATE_CONFIG[state];
  return (
    <div
      title={`${event.title} · ${conf.label}`}
      onClick={e => { e.stopPropagation(); onClick(); }}
      style={{
        position: "absolute", left: left + 2, width: width - 4, top, height: CAL_BAR_H,
        background: conf.bg,
        borderLeft: `3px solid ${conf.color}`,
        borderRadius: 6,
        display: "flex", alignItems: "center",
        padding: "0 8px", overflow: "hidden", cursor: "pointer", boxSizing: "border-box",
      }}>
      <span style={{
        fontSize: 11, fontWeight: 600, color: "#0d2847",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        fontFamily: "var(--font-family-base)", letterSpacing: "-0.1px",
      }}>
        {event.title}
      </span>
    </div>
  );
}

function CalAssetRow({ flatAsset, days, colWidth, today, expanded, onToggle, hasChildren, onDateClick, wos, pmEvents, onWOClick, onPMClick }: {
  flatAsset: FlatAsset;
  days: Date[];
  colWidth: number;
  today: Date;
  expanded: boolean;
  onToggle: () => void;
  hasChildren: boolean;
  onDateClick: (assetId: string, assetName: string, date: string) => void;
  wos: CalWO[];
  pmEvents: CalPMEvent[];
  onWOClick: (wo: CalWO, assetName: string) => void;
  onPMClick: (pm: CalPMEvent, assetName: string) => void;
}) {
  const { asset, depth } = flatAsset;
  const todayIso = calIso(today);
  const rangeStart = calIso(days[0]);
  const rangeEnd = calIso(days[days.length - 1]);

  const visibleWOs = wos.filter(wo =>
    wo.assetId === asset.id &&
    wo.scheduledDate <= rangeEnd &&
    wo.dueDate >= rangeStart
  );
  const visiblePMs = pmEvents.filter(pm =>
    pm.assetId === asset.id &&
    pm.triggerDate >= rangeStart &&
    pm.triggerDate <= rangeEnd
  );

  const allItems = [
    ...visibleWOs.map(wo => ({ id: wo.id, start: wo.scheduledDate, end: wo.dueDate })),
    ...visiblePMs.map(pm => ({ id: pm.id, start: pm.triggerDate, end: pm.triggerDate })),
  ];
  const lanes = assignLanes(allItems);
  const maxLane = allItems.length > 0 ? Math.max(...Array.from(lanes.values())) : -1;
  const rowH = Math.max(48, CAL_ROW_VPAD * 2 + (maxLane + 1) * (CAL_BAR_H + CAL_BAR_GAP));

  return (
    <div style={{ display: "flex", borderBottom: "1px solid #f0f2f7" }}>
      {/* Sticky asset name cell */}
      <div style={{
        width: CAL_ASSET_W, minWidth: CAL_ASSET_W,
        position: "sticky", left: 0, zIndex: 1,
        background: "#fff", borderRight: "1px solid #e8eaed",
        display: "flex", alignItems: "center", gap: 6,
        padding: `0 12px 0 ${12 + depth * 20}px`,
        height: rowH, boxSizing: "border-box",
      }}>
        {hasChildren ? (
          <button onClick={onToggle} style={{
            width: 16, height: 16, border: "none", background: "none",
            cursor: "pointer", padding: 0, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </button>
        ) : (
          <div style={{ width: 16, flexShrink: 0 }} />
        )}
        <div style={{
          width: 26, height: 26, borderRadius: 6, flexShrink: 0,
          background: "#e6e8fe",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M11.73 3.39L9.14 6a.75.75 0 000 1.06l2.59 2.59a.75.75 0 001.06 0L15.39 7a.75.75 0 000-1.06L12.8 3.39a.75.75 0 00-1.07 0z" stroke="#0517F8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17.73 9.39l-2.59 2.61a.75.75 0 000 1.06l2.59 2.59a.75.75 0 001.06 0l2.6-2.59a.75.75 0 000-1.06l-2.6-2.61a.75.75 0 00-1.06 0z" stroke="#0517F8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5.73 9.39l-2.59 2.61a.75.75 0 000 1.06l2.59 2.59a.75.75 0 001.06 0l2.6-2.59a.75.75 0 000-1.06L6.8 9.39a.75.75 0 00-1.07 0z" stroke="#0517F8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M11.73 15.39l-2.59 2.61a.75.75 0 000 1.06l2.59 2.59a.75.75 0 001.06 0l2.6-2.59a.75.75 0 000-1.06l-2.6-2.61a.75.75 0 00-1.06 0z" stroke="#0517F8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12, fontWeight: 700, color: "#172b4d",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            fontFamily: "var(--font-family-base)",
          }}>
            {asset.name}
          </div>
          <div style={{ fontSize: 10, fontWeight: 500, color: "#a2aab8", fontFamily: "var(--font-family-base)" }}>
            {asset.serial}
          </div>
        </div>
      </div>

      {/* Date cells area */}
      <div style={{ position: "relative", height: rowH, minWidth: days.length * colWidth, flex: 1 }}>
        {days.map((d, i) => {
          const iso = calIso(d);
          return (
            <CalDateCell
              key={i}
              left={i * colWidth}
              width={colWidth}
              height={rowH}
              isToday={iso === todayIso}
              onClick={() => onDateClick(asset.id, asset.name, iso)}
            />
          );
        })}

        {visibleWOs.map(wo => {
          const startDiff = calDiff(rangeStart, wo.scheduledDate);
          const endDiff = calDiff(rangeStart, wo.dueDate);
          const cs = Math.max(0, startDiff);
          const ce = Math.min(days.length - 1, endDiff);
          if (ce < 0 || cs >= days.length) return null;
          const lane = lanes.get(wo.id) ?? 0;
          return (
            <CalBar
              key={wo.id} wo={wo}
              left={cs * colWidth + 2}
              width={(ce - cs + 1) * colWidth - 4}
              top={CAL_ROW_VPAD + lane * (CAL_BAR_H + CAL_BAR_GAP)}
              clampedLeft={startDiff < 0}
              onClick={() => onWOClick(wo, asset.name)}
            />
          );
        })}

        {visiblePMs.map(pm => {
          const idx = calDiff(rangeStart, pm.triggerDate);
          if (idx < 0 || idx >= days.length) return null;
          const lane = lanes.get(pm.id) ?? 0;
          return (
            <CalPMChip
              key={pm.id} event={pm}
              left={idx * colWidth}
              width={colWidth}
              top={CAL_ROW_VPAD + lane * (CAL_BAR_H + CAL_BAR_GAP)}
              todayIso={todayIso}
              onClick={() => onPMClick(pm, asset.name)}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── Create WO Modal ──────────────────────────────────────────────────────────

interface CreateWOModalProps {
  assetName: string;
  scheduledDate: string;
  onClose: () => void;
  onCreate: (wo: { title: string; type: CalWO["type"]; scheduledDate: string; dueDate: string }) => void;
}

function CreateWOModal({ assetName, scheduledDate, onClose, onCreate }: CreateWOModalProps) {
  const [title, setTitle] = React.useState("");
  const [type, setType] = React.useState<CalWO["type"]>("work-order");
  const [dueDate, setDueDate] = React.useState(scheduledDate);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    onCreate({ title: title.trim(), type, scheduledDate, dueDate: dueDate || scheduledDate });
    onClose();
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 100,
          background: "rgba(17, 24, 39, 0.35)",
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Modal card */}
      <div style={{
        position: "fixed",
        top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 101,
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)",
        width: 440,
        fontFamily: "var(--font-family-base)",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px 16px",
          borderBottom: "1px solid #f0f2f7",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "#e6e8fe",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M6 2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" stroke="#6974fb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 7h6M7 10h6M7 13h4" stroke="#6974fb" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0d2847", lineHeight: "20px" }}>New Work Order</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: "#748094", marginTop: 1 }}>{assetName}</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 28, height: 28, border: "none", background: "#f3f4f6",
            borderRadius: 8, cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center", color: "#748094",
          }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: "20px 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Title */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#172b4d", letterSpacing: "-0.1px" }}>
              Title <span style={{ color: "#ee0064" }}>*</span>
            </label>
            <input
              autoFocus
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Hydraulic System Inspection"
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "9px 12px",
                border: "1.5px solid #e5e9f0", borderRadius: 8,
                fontSize: 13, color: "#172b4d", background: "#fff",
                fontFamily: "var(--font-family-base)", outline: "none",
              }}
              onFocus={e => { e.currentTarget.style.borderColor = "#6974fb"; }}
              onBlur={e => { e.currentTarget.style.borderColor = "#e5e9f0"; }}
            />
          </div>

          {/* Type */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#172b4d", letterSpacing: "-0.1px" }}>Type</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {WO_TYPE_OPTIONS.map(opt => {
                const conf = CAL_WO_TYPE[opt.value];
                const selected = type === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setType(opt.value)}
                    style={{
                      padding: "5px 12px", borderRadius: 20,
                      border: `1.5px solid ${selected ? conf.color : "#e5e9f0"}`,
                      background: selected ? conf.bg : "#fff",
                      fontSize: 12, fontWeight: 700,
                      color: selected ? conf.color : "#748094",
                      cursor: "pointer", fontFamily: "var(--font-family-base)",
                      transition: "all 100ms",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Dates */}
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#172b4d", letterSpacing: "-0.1px" }}>Scheduled Date</label>
              <div style={{
                padding: "9px 12px", border: "1.5px solid #e5e9f0", borderRadius: 8,
                fontSize: 13, color: "#172b4d", background: "#f8f9fc",
                fontFamily: "var(--font-family-base)",
              }}>
                {calFormatDisplayDate(scheduledDate)}
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#172b4d", letterSpacing: "-0.1px" }}>Due Date</label>
              <input
                type="date"
                value={dueDate}
                min={scheduledDate}
                onChange={e => setDueDate(e.target.value)}
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "9px 12px",
                  border: "1.5px solid #e5e9f0", borderRadius: 8,
                  fontSize: 13, color: "#172b4d", background: "#fff",
                  fontFamily: "var(--font-family-base)", outline: "none",
                  cursor: "pointer",
                }}
                onFocus={e => { e.currentTarget.style.borderColor = "#6974fb"; }}
                onBlur={e => { e.currentTarget.style.borderColor = "#e5e9f0"; }}
              />
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{
              padding: "9px 18px", borderRadius: 8,
              border: "1.5px solid #e5e9f0", background: "#fff",
              fontSize: 13, fontWeight: 600, color: "#1a2233",
              cursor: "pointer", fontFamily: "var(--font-family-base)",
            }}>
              Cancel
            </button>
            <button type="submit" disabled={!title.trim()} style={{
              padding: "9px 18px", borderRadius: 8,
              border: "none",
              background: title.trim() ? "#1a07ff" : "#c7caff",
              fontSize: 13, fontWeight: 700, color: "#fff",
              cursor: title.trim() ? "pointer" : "not-allowed",
              fontFamily: "var(--font-family-base)",
              transition: "background 120ms",
            }}>
              Create Work Order
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

// ─── Calendar Legend ──────────────────────────────────────────────────────────

function CalLegendRow({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: color }} />
      <span style={{ fontSize: 12, fontWeight: 500, color: "#1a2233", fontFamily: "var(--font-family-base)" }}>
        {label}
      </span>
    </div>
  );
}

function CalLegend() {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function h(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        height: 32, padding: "0 12px", background: "#fff",
        border: "1px solid var(--color-border-primary)", borderRadius: 8,
        fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
        cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
        fontFamily: "var(--font-family-base)",
      }}>
        Legend
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5l4 4 4-4" /></svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 50,
          background: "#fff", border: "1px solid var(--color-border-primary)",
          borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          minWidth: 190, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10,
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase", color: "#a2aab8", marginBottom: 8, fontFamily: "var(--font-family-base)" }}>
              PM Events
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <CalLegendRow color={CAL_PM_STATE_CONFIG.active.color} label="Active" />
              <CalLegendRow color={CAL_PM_STATE_CONFIG.upcoming.color} label="Upcoming" />
              <CalLegendRow color={CAL_PM_STATE_CONFIG.completed.color} label="Completed" />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase", color: "#a2aab8", marginBottom: 8, fontFamily: "var(--font-family-base)" }}>
              Work Orders
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <CalLegendRow color={CAL_WO_STATE_CONFIG.assigned.color} label="Assigned" />
              <CalLegendRow color={CAL_WO_STATE_CONFIG.unassigned.color} label="Unassigned" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Calendar Detail Modal ────────────────────────────────────────────────────

type CalDetail =
  | { kind: "wo"; wo: CalWO; assetName: string }
  | { kind: "pm"; pm: CalPMEvent; assetName: string };

function CalDetailModal({ detail, todayIso, onClose }: { detail: CalDetail; todayIso: string; onClose: () => void }) {
  const isWO = detail.kind === "wo";
  const woState = isWO ? calWOState(detail.wo) : null;
  const pmState = !isWO ? calPMState(detail.pm, todayIso) : null;
  const stateConf = isWO ? CAL_WO_STATE_CONFIG[woState!] : CAL_PM_STATE_CONFIG[pmState!];
  const title = isWO ? detail.wo.title : detail.pm.title;
  const typeLabel = isWO ? (WO_TYPE_OPTIONS.find(o => o.value === detail.wo.type)?.label ?? detail.wo.type) : "PM Event";

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(17, 24, 39, 0.35)", backdropFilter: "blur(2px)",
      }} />

      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        zIndex: 101, background: "#fff", borderRadius: 16,
        boxShadow: "0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)",
        width: 420, fontFamily: "var(--font-family-base)", overflow: "hidden",
      }}>
        <div style={{
          padding: "20px 24px 16px", borderBottom: "1px solid #f0f2f7",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: stateConf.bg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M6 2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" stroke={stateConf.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 7h6M7 10h6M7 13h4" stroke={stateConf.color} strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0d2847", lineHeight: "20px" }}>{title}</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: "#748094", marginTop: 1 }}>{detail.assetName}</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 28, height: 28, border: "none", background: "#f3f4f6",
            borderRadius: 8, cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center", color: "#748094",
          }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        <div style={{ padding: "20px 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 10px", borderRadius: 20,
              background: stateConf.bg, color: stateConf.color,
              fontSize: 12, fontWeight: 700,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: stateConf.color }} />
              {stateConf.label}
            </span>
            {isWO && detail.wo.draft && (
              <span style={{
                display: "inline-flex", alignItems: "center",
                padding: "4px 10px", borderRadius: 20,
                background: "#f3f4f6", color: "#748094", fontSize: 12, fontWeight: 700,
              }}>
                Draft
              </span>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#748094" }}>Type</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#1a2233" }}>{typeLabel}</span>
            </div>

            {isWO ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#748094" }}>Scheduled Date</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1a2233" }}>{calFormatDisplayDate(detail.wo.scheduledDate)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#748094" }}>Due Date</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1a2233" }}>{calFormatDisplayDate(detail.wo.dueDate)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#748094" }}>Assignee</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1a2233" }}>{detail.wo.assignee ?? "Unassigned"}</span>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#748094" }}>Trigger Date</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1a2233" }}>{calFormatDisplayDate(detail.pm.triggerDate)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#748094" }}>Status</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1a2233" }}>{detail.pm.status}</span>
                </div>
              </>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
            <button onClick={onClose} style={{
              padding: "9px 18px", borderRadius: 8,
              border: "1.5px solid #e5e9f0", background: "#fff",
              fontSize: 13, fontWeight: 600, color: "#1a2233",
              cursor: "pointer", fontFamily: "var(--font-family-base)",
            }}>
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Assets Calendar View ─────────────────────────────────────────────────────

function AssetsCalendarView({
  assets,
  expandedIds,
  onToggle,
}: {
  assets: Asset[];
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  const [calView, setCalView] = React.useState<CalViewMode>("week");
  const [anchor, setAnchor] = React.useState(new Date());
  const [viewDropOpen, setViewDropOpen] = React.useState(false);
  const [draftWOs, setDraftWOs] = React.useState<CalWO[]>([]);
  const [modal, setModal] = React.useState<{ assetId: string; assetName: string; date: string } | null>(null);
  const [detail, setDetail] = React.useState<
    | { kind: "wo"; wo: CalWO; assetName: string }
    | { kind: "pm"; pm: CalPMEvent; assetName: string }
    | null
  >(null);
  const viewDropRef = React.useRef<HTMLDivElement>(null);
  const draftCounter = React.useRef(0);
  const today = new Date();
  const todayIso = calIso(today);

  function handleDateClick(assetId: string, assetName: string, date: string) {
    setModal({ assetId, assetName, date });
  }

  function handleCreate(wo: { title: string; type: CalWO["type"]; scheduledDate: string; dueDate: string }) {
    if (!modal) return;
    draftCounter.current += 1;
    setDraftWOs(prev => [...prev, {
      id: `draft-${draftCounter.current}`,
      assetId: modal.assetId,
      title: wo.title,
      type: wo.type,
      scheduledDate: wo.scheduledDate,
      dueDate: wo.dueDate,
      draft: true,
    }]);
  }

  const allWOs = [...CALENDAR_WOS, ...draftWOs];
  const allPMs = CALENDAR_PM_EVENTS;

  React.useEffect(() => {
    if (!viewDropOpen) return;
    function h(e: MouseEvent) {
      if (viewDropRef.current && !viewDropRef.current.contains(e.target as Node)) setViewDropOpen(false);
    }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [viewDropOpen]);

  const days = calView === "week" ? calWeekDays(anchor)
    : calView === "month" ? calMonthDays(anchor)
    : [new Date(anchor)];

  const colWidth = calView === "week" ? 110 : calView === "month" ? 46 : 400;

  const title = calView === "week" ? calWeekRange(days)
    : calView === "month" ? calMonthYear(anchor)
    : anchor.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  function navPrev() {
    const d = new Date(anchor);
    if (calView === "week") d.setDate(d.getDate() - 7);
    else if (calView === "month") d.setMonth(d.getMonth() - 1);
    else d.setDate(d.getDate() - 1);
    setAnchor(d);
  }

  function navNext() {
    const d = new Date(anchor);
    if (calView === "week") d.setDate(d.getDate() + 7);
    else if (calView === "month") d.setMonth(d.getMonth() + 1);
    else d.setDate(d.getDate() + 1);
    setAnchor(d);
  }

  const flatAssets = flattenAssets(assets, expandedIds);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", background: "#fff" }}>
      {/* Calendar toolbar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 20px", borderBottom: "1px solid #e5e9f0",
        flexShrink: 0, background: "#fff",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CalNavBtn onClick={navPrev}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 4L6 8l4 4" /></svg>
          </CalNavBtn>
          <button onClick={() => setAnchor(new Date())} style={{
            height: 28, padding: "0 12px", background: "#fff",
            border: "1px solid #e5e9f0", borderRadius: 6,
            fontSize: 12, fontWeight: 600, color: "#1a2233", cursor: "pointer",
            fontFamily: "var(--font-family-base)",
          }}>
            Today
          </button>
          <CalNavBtn onClick={navNext}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 4l4 4-4 4" /></svg>
          </CalNavBtn>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#1a2233", marginLeft: 4, fontFamily: "var(--font-family-base)" }}>
            {title}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CalLegend />

          <div ref={viewDropRef} style={{ position: "relative" }}>
            <button onClick={() => setViewDropOpen(o => !o)} style={{
              height: 32, padding: "0 12px", background: "#fff",
              border: "1px solid var(--color-border-primary)", borderRadius: 8,
              fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              fontFamily: "var(--font-family-base)",
            }}>
              {calView === "week" ? "Week" : calView === "month" ? "Month" : "Day"}
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5l4 4 4-4" /></svg>
            </button>
            {viewDropOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 50,
                background: "#fff", border: "1px solid var(--color-border-primary)",
                borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                minWidth: 120, overflow: "hidden",
              }}>
                {(["day", "week", "month"] as CalViewMode[]).map(v => (
                  <button key={v} onClick={() => { setCalView(v); setViewDropOpen(false); }} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    width: "100%", padding: "9px 14px",
                    fontSize: 12, fontWeight: calView === v ? 700 : 500,
                    color: calView === v ? "#1a2233" : "#748094",
                    background: calView === v ? "#f0f2f7" : "transparent",
                    border: "none", cursor: "pointer", fontFamily: "var(--font-family-base)",
                    textTransform: "capitalize",
                  }}>
                    {v}
                    {calView === v && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#6974fb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6l3 3 5-5" /></svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable calendar grid */}
      <div style={{ flex: 1, overflow: "auto" }}>
        <div style={{ minWidth: CAL_ASSET_W + days.length * colWidth, display: "inline-block", minHeight: "100%", width: "100%" }}>
          {/* Header row */}
          <div style={{
            display: "flex", position: "sticky", top: 0, zIndex: 4,
            background: "#fff", borderBottom: "1px solid #e5e9f0",
          }}>
            <div style={{
              width: CAL_ASSET_W, minWidth: CAL_ASSET_W,
              position: "sticky", left: 0, zIndex: 5,
              background: "#fff", borderRight: "1px solid #e8eaed",
              padding: "10px 16px", display: "flex", alignItems: "center", gap: 4,
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1a2233", fontFamily: "var(--font-family-base)" }}>Assets</span>
              <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4l4 4 4-4" /></svg>
            </div>
            {days.map((d, i) => {
              const isToday = calIso(d) === calIso(today);
              const dowIdx = (d.getDay() + 6) % 7;
              return (
                <div key={i} style={{
                  width: colWidth, minWidth: colWidth, boxSizing: "border-box",
                  padding: calView === "month" ? "6px 0" : "8px 0",
                  textAlign: "center",
                  background: isToday ? "#f5f7ff" : "#fff",
                  borderRight: "1px solid #f0f2f7",
                }}>
                  {calView !== "month" && (
                    <div style={{
                      fontSize: 10, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase",
                      color: isToday ? "#6974fb" : "#a2aab8",
                      fontFamily: "var(--font-family-base)",
                    }}>
                      {calView === "day"
                        ? d.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase()
                        : CAL_DAY_NAMES[dowIdx]}
                    </div>
                  )}
                  <div style={{
                    fontSize: calView === "month" ? 11 : 17, fontWeight: 700, lineHeight: "1.2",
                    color: isToday ? "#6974fb" : "#1a2233",
                    fontFamily: "var(--font-family-base)",
                  }}>
                    {calView === "month" && d.getDate() === 1
                      ? d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
                      : d.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Asset rows */}
          {flatAssets.map(fa => (
            <CalAssetRow
              key={fa.asset.id}
              flatAsset={fa}
              days={days}
              colWidth={colWidth}
              today={today}
              expanded={expandedIds.has(fa.asset.id)}
              onToggle={() => onToggle(fa.asset.id)}
              hasChildren={!!(fa.asset.subAssets?.length)}
              onDateClick={handleDateClick}
              wos={allWOs}
              pmEvents={allPMs}
              onWOClick={(wo, assetName) => setDetail({ kind: "wo", wo, assetName })}
              onPMClick={(pm, assetName) => setDetail({ kind: "pm", pm, assetName })}
            />
          ))}
        </div>
      </div>

      {modal && (
        <CreateWOModal
          assetName={modal.assetName}
          scheduledDate={modal.date}
          onClose={() => setModal(null)}
          onCreate={handleCreate}
        />
      )}

      {detail && (
        <CalDetailModal
          detail={detail}
          todayIso={todayIso}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

// ─── Page Header Actions ──────────────────────────────────────────────────────

function HeaderActions({ onAskAI, askAIActive }: { onAskAI: () => void; askAIActive: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button
        onClick={onAskAI}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          height: 40,
          padding: "0 10px",
          background: askAIActive ? "#e6e8fe" : "var(--color-bg-primary)",
          border: `1px solid ${askAIActive ? "#0517f8" : "var(--color-border-primary)"}`,
          borderRadius: "var(--radius-s)",
          fontFamily: "var(--font-family-base)",
          fontSize: 12,
          fontWeight: 700,
          lineHeight: "16px",
          letterSpacing: "-0.24px",
          color: askAIActive ? "#0517f8" : "var(--color-content-primary)",
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
          transition: "all 120ms ease",
        }}
      >
        <AskAIIcon />
        Ask AI
      </button>
      <button
        aria-label="Search"
        style={{
          width: 40,
          height: 40,
          background: "var(--color-bg-accent)",
          border: "none",
          borderRadius: "var(--radius-s)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <SearchIcon />
      </button>
      <button
        aria-label="Import"
        style={{
          width: 40,
          height: 40,
          background: "var(--color-bg-primary)",
          border: "1px solid var(--color-border-primary)",
          borderRadius: "var(--radius-s)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <ImportIcon />
      </button>
      <button
        style={{
          height: 40,
          padding: "0 var(--spacing-s)",
          background: "var(--color-bg-brand-dark)",
          border: "none",
          borderRadius: "var(--radius-s)",
          fontFamily: "var(--font-family-base)",
          fontSize: 12,
          fontWeight: 700,
          lineHeight: "16px",
          letterSpacing: "-0.24px",
          color: "var(--color-content-inverse)",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Add Assets
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function AssetsListPage({ onOpenAsset }: { onOpenAsset?: (id: string, name: string) => void }) {
  const [view, setView] = React.useState<ViewType>("list");
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set(["1", "1-1", "2"]));
  const [askAIOpen, setAskAIOpen] = React.useState(false);

  function toggleExpanded(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const tabs = [
    { id: "list"     as ViewType, label: "List",     Icon: ListViewIcon },
    { id: "table"    as ViewType, label: "Table",    Icon: TableViewIcon },
    { id: "board"    as ViewType, label: "Board",    Icon: BoardViewIcon },
    { id: "map"      as ViewType, label: "Map",      Icon: MapViewIcon },
    { id: "calendar" as ViewType, label: "Calendar", Icon: CalendarViewIcon },
  ];

  return (
    <div style={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      background: "var(--color-bg-primary)",
    }}>
      <PageHeader
        breadcrumbs={[{ label: "Assets" }]}
        actions={<HeaderActions onAskAI={() => setAskAIOpen(o => !o)} askAIActive={askAIOpen} />}
      />

      {/* Toolbar: view tabs + filters */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px var(--spacing-2xl)",
        borderBottom: "1px solid var(--color-border-primary)",
        background: "var(--color-bg-primary)",
        flexShrink: 0,
      }}>
        {/* Pill view tabs */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {tabs.map(({ id, label, Icon }) => {
            const active = view === id;
            return (
              <button
                key={id}
                onClick={() => setView(id)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "6px 8px", borderRadius: 4,
                  border: "none", cursor: "pointer",
                  background: active ? "#e6e8fe" : "transparent",
                  fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
                  color: active ? "var(--color-content-primary)" : "#a2aab8",
                  fontFamily: "var(--font-family-base)", transition: "all 100ms",
                }}
              >
                <Icon active={active} />
                {label}
              </button>
            );
          })}
        </div>

        {view !== "calendar" && (
          <button style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 12px",
            border: "1px solid var(--color-border-primary)",
            borderRadius: 8, background: "var(--color-bg-primary)",
            fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
            color: "var(--color-content-primary)",
            fontFamily: "var(--font-family-base)", cursor: "pointer", flexShrink: 0,
          }}>
            <FilterIcon />
            Filters
          </button>
        )}
      </div>

      {/* Content area */}
      {view === "calendar" ? (
        <div style={{ flex: 1, overflow: "hidden" }}>
          <AssetsCalendarView
            assets={MOCK_ASSETS}
            expandedIds={expandedIds}
            onToggle={toggleExpanded}
          />
        </div>
      ) : (
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "var(--spacing-l) var(--spacing-2xl)",
          background: "var(--color-bg-primary)",
        }}>
          {view === "list" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {MOCK_ASSETS.map(asset => (
                <AssetRow
                  key={asset.id}
                  asset={asset}
                  expandedIds={expandedIds}
                  onToggle={toggleExpanded}
                  onOpen={onOpenAsset}
                />
              ))}
            </div>
          )}

          {view !== "list" && (
            <div style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              minHeight: 400,
              color: "#748094",
              fontFamily: "var(--font-family-base)",
            }}>
              <span style={{ fontSize: 32 }}>🚧</span>
              <p style={{ fontSize: 14, fontWeight: 700, color: "#172b4d", margin: 0 }}>
                {view === "table" ? "Table" : view === "board" ? "Board" : "Map"} View
              </p>
              <p style={{ fontSize: 13, fontWeight: 500, color: "#748094", margin: 0 }}>
                This view is coming soon.
              </p>
            </div>
          )}
        </div>
      )}

      {askAIOpen && (
        <AskAIPanel onClose={() => setAskAIOpen(false)} />
      )}
    </div>
  );
}
